import React from 'react';
import {connect} from 'react-redux';
import {deepClone, forEach, has, sortBy} from 'lodash';

// local imports
import {Link} from "react-router-dom";
import api from '../../../store/api'
import {toggleGlobalLoader, addErrorAlert, addLoadingAlert, removeLoadingAlert} from "../../../store/calls/alertLoadingDispatch";

import AdminWrapper from "../../theme/wrappers/AdminWrapper";
import validators from "../../../utils/forms/validators";
import formatters from "../../../utils/forms/formatters";
import {nullBooleanSelect, slugInput } from "../../../utils/forms/preconfiguredInputs";

import Input from "../../theme/forms/Input";
import BaseForm from "../../theme/forms/BaseForm";
import PaginatedTable from "../../theme/tables/PaginatedTable";
import ConfirmModal from "../../theme/modals/ConfirmModal";
import FormModal from "../../theme/modals/FormModal";
import PacefiFullScreenSpinner from "../../theme/loaders/pacefiFullScreenSpinner";
import {getUUID} from "../../../utils/hashFuncs";
import BaseModal from '../../theme/modals/BaseModal';
import SelectByCategory from '../../theme/forms/SelectByCategory';

export default class ImprovementAdminScreen extends React.Component {

    constructor(props) {
        super(props);

        this.state = {

            displayedData: [],
            dataPullLimit: 25,
            dataPullOffset: 0,

            // edit and delete
            editModalOpen: false,
            deleteModalOpen: false,
            editingInstance: null,

            // product modal
            linkProductModalOpen: false,
            productData: [],
            selectedImprovementExclusions: [],
            selectedImprovementId: -1,
            fundersData: [],

            mode: 'improvements', // can also be categories

            improvementCategories: [],

            categoryFilter: null,
            nameFilter: null,
        }

        this.createEditInputs = [
            {
                type: 'text',
                label: "Name",
                dataKey: "name",
                ...validators.isStringOfMinLength(3)
            },
            {
                type: 'select',
                label: "Category",
                dataKey: 'category',
                ...validators.exists
            },
            {...slugInput},
            {
                type: 'description',
                label: "Description",
                dataKey: "description",
            },
            {
                type: 'select',
                label: "Residential",
                dataKey: "residential",
                ...nullBooleanSelect
            },
            {
                type: 'text',
                label: "Maximum Term (in Years)",
                dataKey: "max_term",
                placeholder: 'e.g. 60',
                ...validators.isNumber
            },
            {
                type: 'text',
                label: "Tax Benefit Percentage (%)",
                dataKey: "tax_benefit_percentage",
                ...validators.isNumber,
            },
            {
                type: 'text',
                label: "Tax Benefit Fixed Subsidy ($)",
                dataKey: "tax_benefit_fixed_subsidy",
                ...validators.isNumber,
                ...formatters.USDCurrency
            },
            {
                type: 'text',
                label: "Direct Pay Percentage (%)",
                dataKey: "direct_pay_percentage",
                ...validators.isNumber,
            },

        ]

    }

    componentDidMount() {
        toggleGlobalLoader(true);

        Promise.all([
            this.getData(),
            this.getCategories(),
            this.getProducts(),
            this.getFunders(),
        ])
            .then(() => toggleGlobalLoader(false))
            .catch(() => addErrorAlert('Failed to load all initial data.', 10 * 1000))

    }

    toggleEditModal = () => {
        this.setState({editModalOpen: !this.state.editModalOpen})
    }

    getData = (xtraParams = {}) => {

        let params = {
            limit: this.state.dataPullLimit,
            offset: this.state.dataPullOffset,
            name: this.state.nameFilter,
            category: this.state.categoryFilter ? this.state.categoryFilter : null,
            bustCache: getUUID(), // bust cache so we refresh correctly
            ...xtraParams
        };

        return api.get('/improvements/', params)
            .then(res => {
                this.setState({displayedData: res.data.results})
            })
    }

    getCategories = (xtraParams = {}) => {
        return api.get('/improvement-categories/', xtraParams)
            .then(res => {
                this.setState({improvementCategories: res.data})
            })
    }

    getProducts = (xtraParams = {}) => {
        return api.get('/loan-products/', xtraParams)
            .then(res => {
                this.setState({productData: res.data})
            })
    }

    getFunders = (xtraParams = {}) => {
        return api.get('/funders/', xtraParams)
            .then(res => {
                this.setState({fundersData: res.data})
            })
    }

    handleLimitChange = (val) => {
        this.setState({dataPullLimit: val}, this.getData)
    }

    handleOffsetChange = (val) => {
        this.setState({dataPullOffset: val}, this.getData)
    }

    populateInputsWithValues = () => {

        return this.createEditInputs.map(d => {
            if (d.dataKey === 'category') {
                d.options = [{value: 0, display: 'All Categories'},
                    ...sortBy(this.state.improvementCategories, ['name']).map(d => ({value: d.id, display: d.name}))
                ]

                return {
                    ...d,
                    value: this.state.editingInstance ?
                        (this.state.improvementCategories.find(c =>this.state.editingInstance[d.dataKey] === c.name) || {}).id
                        : null
                }

            } 
            return {
                ...d,
                value: this.state.editingInstance ? this.state.editingInstance[d.dataKey] : null,
                disabled: !!(this.state.editingInstance?.id && d.dataKey == 'slug'),
            }
            
        })

    }

    handleDeleteRequest = () => {
        addLoadingAlert();
        api.patch(`/improvements/${this.state.editingInstance.id}/`, {is_active: 0})
            .then(() => this.getData())
            .catch(err => addErrorAlert('Failed to delete.'))
            .finally(() => this.setState({deleteModalOpen: false, editingInstance: null}, removeLoadingAlert))
    }

    handleCreateOrUpdateRequest = (data) => {
        let promise; let err_msg;
        if (this.state.editingInstance && !this.state.editingInstance.bogusKey) {
            promise = () => api.patch(`/improvements/${this.state.editingInstance.id}/`, data)
            err_msg = "Failed to update."
        } else {
            promise = () => api.post('/improvements/', data)
            err_msg = "Failed to create."
        }

        return promise()
            .then(() => this.getData())
            .catch(err => addErrorAlert(err_msg))
            .finally(() => this.setState({editModalOpen: false, editingInstance: null}, removeLoadingAlert))
    }


    handleSearch = (val) => {
        this.setState({nameFilter: val},
            this.getData
        )
    }

    buildFilterOptions = () => {
        return [
            {
                options: [
                    {value: 0, display: 'All Categories'},
                    ...sortBy(this.state.improvementCategories, ['name']).map(d => ({value: d.id, display: d.name}))
                ],
                value: this.state.categoryFilter, // default ca
                onChange: (val) => this.setState({categoryFilter: +val}, this.getData)
            }
        ]
    }

    handleOpenLinkProductModal = () => {
        return api.get(`/loan-product-improvement-exclusions/?improvement=${this.state.selectedImprovementId}`)
            .then(res => {
                this.setState({selectedImprovementExclusions: res.data})
                this.setState({productData: this.state.productData.map(p => ({
                    ...p,
                    funder_name: p.funder.name,
                    selected: p.selected === undefined ? this.state.selectedImprovementExclusions.find(i => i.loan_product == p.id) === undefined : p.selected
                }))})
            })
            .then(this.setState({linkProductModalOpen: true}))
    }

    handleImprovementExclusion = (selected, id) => {
        this.handleBulkImprovementExclusion(selected, [id])
    }

    handleBulkImprovementExclusion = (selected, ids = []) => {
        let promise

        if (selected) {
            let matching_exclusions = this.state.selectedImprovementExclusions.filter(d => ids.includes(d.loan_product))
            promise = Promise.all(matching_exclusions.map(d => api.delete(`/loan-product-improvement-exclusions/${d.id}/`)))
        } else {
            promise = Promise.all(ids.map(id => (
                    api.post(`/loan-product-improvement-exclusions/`, {
                        improvement: this.state.selectedImprovementId,
                        loan_product: id
                    })
            )))
        }

        return promise
            .then(() => {
                this.setState({productData: this.state.productData.map(d => ({
                    ...d,
                    selected: ids.includes(d.id) ? selected : d.selected,
                }))})
            })
            .then(this.handleOpenLinkProductModal)
    }

    handleCloseLinkProductModal = () => {
        this.setState({linkProductModalOpen: false})
    }

    render() {
        return (
            <AdminWrapper>
                <div className="section">
                    <div className="container is-fullhd mx-6">
                        <div className="level">
                            <div className="level-left">
                                <div className="level-item">
                                    <h2 className="title is-2">Manage Improvements</h2>
                                </div>
                            </div>
                            <div className="level-right">
                                <div className="level-item">
                                    {/* We have to set  fake editingInstance here to force a reset of values in the form */}
                                    <button
                                        className='button is-link'
                                        onClick={() => this.setState({editModalOpen: true, editingInstance: {bogusKey: 1}})}
                                    >+ Create Improvement</button>
                                </div>
                                <div className="level-item">
                                    <Link to="/admin/improvements/categories">
                                        <button
                                            className='button'
                                            onClick={() => null}
                                        >Manage Categories</button>
                                    </Link>
                                </div>
                                <div className="level-item">
                                    <Link to="/admin/improvements">
                                        <button
                                            className='button is-info'
                                            onClick={() => null}
                                        >Back
                                        </button>
                                    </Link>
                                </div>
                            </div>
                        </div>
                        <PaginatedTable
                            className="is-fullwidth is-hoverable"
                            data={sortBy(this.state.displayedData, 'name')}
                            columns={['name', 'description', 'category', 'residential', 'max_term',
                                'direct_pay_percentage', 'tax_benefit_percentage', 'tax_benefit_fixed_subsidy']}
                            pullLimit={this.state.dataPullLimit}
                            handleLimitChange={this.handleLimitChange}
                            pullOffset={this.state.dataPullOffset}
                            handleOffsetChange={this.handleOffsetChange}
                            handleSearch={this.handleSearch}
                            filterOptions={this.buildFilterOptions()}
                            actions={[
                                {
                                    name: 'Link Product',
                                    elem: (<span className="icon has-text-info">
                                                <i className="fas fa-plus"/>
                                            </span>),
                                    action: ({id}) => this.setState({selectedImprovementId: id}, this.handleOpenLinkProductModal)
                                },
                                {
                                    name: 'Edit',
                                    elem: (<span className="icon has-text-info">
                                                <i className="fas fa-edit"/>
                                            </span>),
                                    action: (data) => this.setState({editModalOpen: true, editingInstance: {...data}})
                                },
                                {
                                    name: 'Delete',
                                    elem: (<span className="icon has-text-danger">
                                                <i className="fas fa-minus-square"/>
                                            </span>),
                                    action: (data) => this.setState({deleteModalOpen: true, editingInstance: {...data}})
                                }
                            ]}
                        />
                    </div>
                </div>

                <BaseModal
                    visible={this.state.linkProductModalOpen}
                    cardStyle={{width: 640, height: 800,  display: 'flex', justifyContent: 'center'}}
                >
                    <h5 className="title is-5">Exclude Loan Products from Improvement</h5>
                    <SelectByCategory
                        data={this.state.productData}
                        categoryKey="funder_name"
                        onBulkOptionValueChange={this.handleBulkImprovementExclusion}
                        onOptionValueChange={this.handleImprovementExclusion}
                    />
                    <div className='py-4'>
                        <button className='button is-primary' onClick={this.handleCloseLinkProductModal}>Close</button>
                    </div>
                </BaseModal>
                <FormModal
                    title={this.props.editingInstance ? this.props.editingInstance.name : 'Create a new Improvement'}
                    visible={this.state.editModalOpen}
                    inputs={this.populateInputsWithValues()}
                    disableAutocomplete
                    onSubmit={this.handleCreateOrUpdateRequest}
                    onCancel={() => this.setState({editModalOpen: false, editingInstance: null})}
                    // otherButtons={[<button className={'button'} onClick={e() => null}>Add Another</button>]}
                />
                <ConfirmModal
                    type="warning"
                    visible={this.state.deleteModalOpen && this.state.editingInstance}
                    prompt={!!this.state.editingInstance && `Are you sure you want to delete improvement ${this.state.editingInstance.name}?`}
                    onCancel={() => this.setState({deleteModalOpen: false, editingInstance: null})}
                    onAccept={this.handleDeleteRequest}
                />

            </AdminWrapper>
        )
    }

}
